import { Dropdown, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import {
    MdOutlineDarkMode,
    MdOutlineLightMode,
    MdOutlineLogout,
    MdOutlinePerson,
    MdPhotoCamera,
    MdUnfoldMore,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import { userManager } from '@/Core/Managers'
import { GetRouteUrlByKeyTask } from '@/Core/Tasks/UI/Menu/GetRouteUrlByKeyTask'
import { UserAvatarContent } from '@/Ship/Components/UI/UserPhoto/UserAvatarContent'
import photoStyles from '@/Ship/Components/UI/UserPhoto/UserPhoto.module.css'
import { useUserPhotoEditor } from '@/Ship/Components/UI/UserPhoto/useUserPhotoEditor'
import { unsubscribeFromPush } from '@/Ship/Pwa/ServiceWorkerRegistrar'
import { themeStore } from '@/Ship/Stores/ThemeStore'

import styles from './UserProfileBlock.module.css'
import { formatPhone } from '@/Ship/Helpers/formatPhone'

/** Підписи ролей (backend config/packages/security.yaml) у порядку пріоритету: показуємо першу знайдену */
const ROLE_LABELS: Array<[string, string]> = [
    ['ROLE_SUPER_ADMIN', 'Суперадміністратор'],
    ['ROLE_ADMIN', 'Адміністратор'],
    ['ROLE_SENIOR_DISPATCHER', 'Старший диспетчер'],
    ['ROLE_DISPATCHER', 'Диспетчер'],
    ['ROLE_MECHANIC', 'Механік'],
    ['ROLE_GUARD', 'Охорона'],
    ['ROLE_DRIVER', 'Водій'],
    ['ROLE_VIEWER', 'Спостерігач'],
]

export function resolveRoleLabel(roles: unknown): string {
    if (!Array.isArray(roles)) return 'Користувач'
    return ROLE_LABELS.find(([role]) => roles.includes(role))?.[1] ?? 'Користувач'
}

export function resolveInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '?'
    return parts
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('')
}

/** 380671234567 → +380 (67) 123-45-67; інше повертаємо як є */
/** @deprecated Імпортуйте з `@/Ship/Helpers/formatPhone`; тут лишено для сумісності */
export { formatPhone }

interface UserProfileBlockProps {
    collapsed: boolean
}

/**
 * Блок поточного користувача внизу сайдбара: аватар (фото або ініціали), ім'я, роль.
 * Клік відкриває панель з даними, перемикачем теми, профілем і виходом; клік по
 * аватару в панелі змінює фото (вибір файлу → обрізання → збереження).
 * У згорнутому сайдбарі лише аватар.
 */
export const UserProfileBlock = observer(({ collapsed }: UserProfileBlockProps) => {
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const user = (userManager.user ?? {}) as Record<string, unknown>
    const name = String(user['fullName'] || user['phone'] || '') || 'Користувач'
    const phone = user['phone'] ? formatPhone(String(user['phone'])) : ''
    const role = resolveRoleLabel(user['roles'])
    const initials = resolveInitials(name)
    const photoEditor = useUserPhotoEditor()
    const photoLabel = photoEditor.hasPhoto ? 'Змінити фото' : 'Додати фото'
    const isDark = themeStore.mode === 'dark'

    const goToProfile = () => {
        setOpen(false)
        const url = GetRouteUrlByKeyTask('Setting.Profile')
        if (url) navigate(url)
    }

    const logout = async () => {
        setOpen(false)
        // Відписуємо пристрій від push, поки токен ще чинний.
        await unsubscribeFromPush()
        userManager.logout()
    }

    const panel = (
        <div className={styles['panel']} role="menu" aria-label="Меню користувача">
            <div className={styles['panelHeader']}>
                {/* Клік по аватару змінює фото; панель закриваємо, щоб не заважала вікну обрізання */}
                <button
                    type="button"
                    className={`${photoStyles['changer']}${photoEditor.busy ? ` ${photoStyles['changerBusy']}` : ''}`}
                    aria-label={photoLabel}
                    title={photoLabel}
                    disabled={photoEditor.busy}
                    onClick={() => {
                        setOpen(false)
                        photoEditor.pick()
                    }}
                >
                    <span className={`${styles['avatar']} ${styles['avatarLarge']}`} aria-hidden="true">
                        <UserAvatarContent photo={user['photo']} initials={initials} />
                    </span>
                    <span className={photoStyles['changerOverlay']} aria-hidden="true">
                        <MdPhotoCamera />
                    </span>
                </button>
                <span className={styles['panelText']}>
                    <span className={styles['panelName']}>{name}</span>
                    {phone && <span className={styles['panelMeta']}>{phone}</span>}
                    <span className={styles['roleBadge']}>{role}</span>
                </span>
            </div>

            <div className={styles['panelItems']}>
                <button type="button" role="menuitem" className={styles['item']} onClick={goToProfile}>
                    <MdOutlinePerson className={styles['itemIcon']} />
                    Мій профіль
                </button>
                {/* Тема перемикається без закриття панелі, щоб одразу бачити результат */}
                <button type="button" role="menuitem" className={styles['item']} onClick={() => themeStore.toggle()}>
                    {isDark ? (
                        <MdOutlineLightMode className={styles['itemIcon']} />
                    ) : (
                        <MdOutlineDarkMode className={styles['itemIcon']} />
                    )}
                    {isDark ? 'Світла тема' : 'Темна тема'}
                    <span className={styles['itemHint']}>{isDark ? 'зараз темна' : 'зараз світла'}</span>
                </button>
            </div>

            <div className={styles['panelItems']}>
                <button
                    type="button"
                    role="menuitem"
                    className={`${styles['item']} ${styles['itemDanger']}`}
                    onClick={logout}
                >
                    <MdOutlineLogout className={styles['itemIcon']} />
                    Вийти
                </button>
            </div>
        </div>
    )

    const trigger = (
        <button
            type="button"
            className={`${styles['block']}${collapsed ? ` ${styles['collapsed']}` : ''}${open ? ` ${styles['open']}` : ''}`}
            aria-label="Меню користувача"
            aria-haspopup="menu"
            aria-expanded={open}
        >
            <span className={styles['avatar']} aria-hidden="true">
                <UserAvatarContent photo={user['photo']} initials={initials} />
            </span>
            {!collapsed && (
                <>
                    <span className={styles['text']}>
                        <span className={styles['name']}>{name}</span>
                        <span className={styles['role']}>
                            <span className={styles['roleDot']} />
                            {role}
                        </span>
                    </span>
                    <MdUnfoldMore className={styles['chevron']} aria-hidden="true" />
                </>
            )}
        </button>
    )

    return (
        <div className={`${styles['footer']}${collapsed ? ` ${styles['footerCollapsed']}` : ''}`}>
            <Dropdown
                open={open}
                onOpenChange={setOpen}
                trigger={['click']}
                placement={collapsed ? 'topLeft' : 'top'}
                popupRender={() => panel}
            >
                {collapsed ? (
                    <Tooltip title={name} placement="right" open={open ? false : undefined}>
                        {trigger}
                    </Tooltip>
                ) : (
                    trigger
                )}
            </Dropdown>
            {photoEditor.holder}
        </div>
    )
})
